import React, { useContext, useCallback, useMemo, useEffect, useState } from 'react';
import clsx from 'clsx';
import { CircularProgress, makeStyles, Theme } from '@material-ui/core';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, setIsEditingForm, showDialog, showErrorNotification,
    showSuccessNotification } from '@moderepo/mode-ui-state';
import { BaseCompAction, capitalizeAll, SlideOutComp } from '@moderepo/mode-app-components';
import { ApiError, AppAPI, GatewayDefinition, RequestMethod, RobotCloudAPI } from '@moderepo/mode-apis';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchRCProjectGatewayCatalog, rcDataContext, selectProjectGatewayCatalog } from '@moderepo/mode-data-state-robot-cloud';
import { RitzAPI } from '@moderepo/mode-internal-apis';
import { getGatewayConfigKVStoreKey } from '@moderepo/mode-app-components-internal';
import { RCGatewayDefinitionInfo } from '../../../components/robot-cloud';
import { RCGatewayDefinitionInfoProps } from '../../../components/robot-cloud/admin/RCGatewayDefinitionInfo';
import { useRitzTranslation } from '../../../utils/customHooks';


const useStyle = makeStyles((theme: Theme) => {
    return {
        secondaryAction: {
            '&.remove': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});


interface GatewayDefinitionPreviewContainerProps {
    readonly projectId: number;
    readonly deviceClass: string | undefined;
}


/**
 * This Component is used for displaying HomeInfo on the preview panel for preview. It does not show all home details e.g. devices, logs,
 * kv pairs, etc... but only the Home object details
 */
export const RCGatewayDefinitionPreviewContainer = (props: GatewayDefinitionPreviewContainerProps) => {
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);
    const isNewGatewayDef = props.deviceClass === undefined;
    const gatewayCatalog = selectProjectGatewayCatalog(rcDataState, projectId);
    const selectedGatewayDef = props.deviceClass && gatewayCatalog ? gatewayCatalog.find((gatewayDef: GatewayDefinition) => {
        return gatewayDef.deviceClass === props.deviceClass;
    }) : undefined;
    const classes = useStyle();


    const [isEditing, setIsEditing] = useState<boolean>();
    const [inputError, setInputError] = useState<string | undefined>();


    const fetchGatewayCatalog = useCallback(() => {
        rcDataDispatch(fetchRCProjectGatewayCatalog(projectId)).catch(() => { /* ignore error */ });
    }, [projectId, rcDataDispatch]);


    /**
     * This useEffect used for loading gateway catalog on component init
     */
    useEffect(() => {
        if (!gatewayCatalog) {
            fetchGatewayCatalog();
        }
    }, [dataDispatch, props.deviceClass, selectedGatewayDef, gatewayCatalog, projectId, fetchGatewayCatalog]);


    /**
     * Unselect the selected gateway definition and update the URL
     */
    const unselectGatewayDefinition = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputError(undefined);

        // also change global edit mode
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (isNewGatewayDef) {
            unselectGatewayDefinition();
        }
    }, [closeEditing, isNewGatewayDef, unselectGatewayDefinition]);



    /**
     * Update all gateway instances' driver settings if the gateway definition's drivers settings changed.
     */
    const updateGatewayDriverSettings = useCallback(async (
        deviceClass: string,
        oldDriverSettings: GatewayDefinition['drivers'] | undefined,
        newDriverSettings: GatewayDefinition['drivers'],
    ) => {
        // If the string representation of the driver settings are not the same then we can assume the settings changed.
        // We can compare props by props but that is too much work
        if (JSON.stringify(oldDriverSettings) !== JSON.stringify(newDriverSettings)) {

            // fetch all the devices of the specified deviceClass
            const devices = await RitzAPI.getInstance().getProjectDevices(props.projectId, undefined, deviceClass).catch(() => {
                // Unable to fetch project devices. Ignore errors and treat it as no devices. Nothing we can do if we are unable to fetch devices.
            });

            if (devices && devices.items.length > 0) {

                // Each of these devices might have a config stored in DDP under this naming convention GATEWAY_CONFIG_[device_id]
                // so we need to search for these DDP and replace the 'drivers' settings in the DDP with the new driver settings
                for (let i = 0; i < devices.items.length; i += 1) {
                    const device = devices.items[i];

                    // This will be something like this GATEWAY_CONFIG_1234
                    const ddpKey = getGatewayConfigKVStoreKey(device.id);

                    try {
                        // Fetch the KV store that is used for storing the Gateway config for the device. NOTE that this KV might not exist
                        // if this device is not configured. SO it is possible that this call will fail.

                        // eslint-disable-next-line no-await-in-loop
                        const deviceKVStore = await AppAPI.getInstance().getDeviceKeyValuePair(device.id, ddpKey);

                        if (deviceKVStore && deviceKVStore.value) {
                            // Create a new config by coping the current KV Store's 'value' and replace the value.drivers with the new drivers
                            const newConfig = {
                                ...deviceKVStore.value,
                                drivers: newDriverSettings,
                            };

                            // Save the new config
                            AppAPI.getInstance().updateDeviceKeyValuePair(device.id, ddpKey, newConfig);
                        }
                    } catch (error) {
                        // this device does not have a DDP for the Gateway config so don't need to do anything
                    }
                }
            }
        }
    }, [props.projectId]);



    const onRemoveGatewayDefActionClicked = useCallback((gatewayDef: GatewayDefinition) => {
        // Confirm before removing home
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'Remove Gateway Definition',
            message       : 'Are you sure you want to remove the gateway definition for deviceClass \'{{device_class}}\'',
            messageData   : { device_class: gatewayDef.deviceClass },
            positiveButton: 'label.remove',
            negativeButton: 'label.cancel',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    RobotCloudAPI.getInstance().sendRequest(
                        RequestMethod.DELETE,
                        `/projects/${projectId}/appProxy/rcp/deviceDefinitions/${gatewayDef.deviceClass}`,
                    ).then(() => {
                        uiDispatch(showSuccessNotification('Gateway Catalog Deleted successfully'));
                        closeEditing();
                        unselectGatewayDefinition();

                        // Refetch gateway catalog
                        fetchGatewayCatalog();
                    }).catch((error: ApiError) => {
                        uiDispatch(showErrorNotification(error.message));
                    });
                }
            },
        }));
    }, [closeEditing, fetchGatewayCatalog, projectId, uiDispatch, unselectGatewayDefinition]);



    const onFormSubmit = useCallback((inputs: {
        readonly gatewayDef: string | undefined
    }) => {
        const { gatewayDef } = inputs;

        if (!gatewayDef) {
            setInputError('Gateway definition is required');
        } else {

            try {
                // try to parse gatewayDef to make sure it is valid JSON
                const updatedGatewayDef = JSON.parse(gatewayDef) as GatewayDefinition;

                if (isNewGatewayDef) {
                    // Create new Gateway definition
                    RobotCloudAPI.getInstance().sendRequest(
                        RequestMethod.POST,
                        `/projects/${projectId}/appProxy/rcp/deviceDefinitions`, updatedGatewayDef,
                    ).then(() => {
                        uiDispatch(showSuccessNotification('Gateway Catalog created successfully'));
                        closeEditing();

                        unselectGatewayDefinition();

                        // Refetch gateway catalog
                        fetchGatewayCatalog();
                    }).catch((error: ApiError) => {
                        uiDispatch(showErrorNotification(error.message));
                    });
                } else {

                    // Update existing gateway definition
                    RobotCloudAPI.getInstance().sendRequest(
                        RequestMethod.PATCH,
                        `/projects/${projectId}/appProxy/rcp/deviceDefinitions/${selectedGatewayDef?.deviceClass}`, updatedGatewayDef,
                    ).then(() => {
                        uiDispatch(showSuccessNotification('Gateway Catalog saved successfully'));
                        closeEditing();

                        // Refetch gateway catalog
                        fetchGatewayCatalog();

                        // Call updateGatewayDriverSettings to take care of updating all gateway instance's driver settings if it changed
                        updateGatewayDriverSettings(updatedGatewayDef.deviceClass, selectedGatewayDef?.drivers, updatedGatewayDef.drivers);

                    }).catch((error: ApiError) => {
                        uiDispatch(showErrorNotification(error.message));
                    });
                }
            } catch (error) {
                setInputError((error as Error).message);
            }
        }
    }, [closeEditing, fetchGatewayCatalog, isNewGatewayDef, projectId, selectedGatewayDef, uiDispatch, unselectGatewayDefinition,
        updateGatewayDriverSettings]);



    const gatewayDefinitionInfoSettings = useMemo((): RCGatewayDefinitionInfoProps['fields'] => {
        return {
            gatewayDef: {
                label   : 'Gateway Definition',
                editable: true,
                value   : selectedGatewayDef ? JSON.stringify({
                    ...selectedGatewayDef,
                    createdAt     : undefined,
                    createdBy     : undefined,
                    updatedAt     : undefined,
                    createdAgentId: undefined,
                    updatedAgentId: undefined,
                }, undefined, 4) : undefined,
                error: inputError || '',
            },
            creationTime: {
                label : 'Created Date',
                value : selectedGatewayDef?.createdAt,
                hidden: isNewGatewayDef,
            },
        };
    }, [inputError, isNewGatewayDef, selectedGatewayDef]);


    const secondaryActions = useMemo(() => {
        if (selectedGatewayDef) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'remove'),
                icon     : 'delete',
                label    : 'Remove Gateway Definition',
                onClick  : () => {
                    onRemoveGatewayDefActionClicked(selectedGatewayDef);
                },
            }];
            return actions;
        }
        return undefined;
    }, [classes.secondaryAction, onRemoveGatewayDefActionClicked, selectedGatewayDef]);


    /**
     * Validate the value string and make sure it is a valid JSON, reformat the string and return the formatted string using 4 spaces tab.
     * Throw an error if the JSON is invalid.
     */
    const onValidateJSON = useCallback((value: string): string => {
        return JSON.stringify(JSON.parse(value), undefined, 4);
    }, []);


    const slideOutCompContent = useMemo(() => {
        if (isNewGatewayDef || selectedGatewayDef) {
            return (
                <RCGatewayDefinitionInfo
                    className={clsx(isEditing && 'editing')}
                    title="Gateway Definition"
                    icon="router"
                    fields={gatewayDefinitionInfoSettings}
                    isEditing={isEditing}
                    actions={{
                        formatJson: {
                            label  : capitalizeAll(trans('label.format_json')),
                            onClick: onValidateJSON,
                        },
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : isNewGatewayDef ? capitalizeAll(trans('label.create')) : capitalizeAll(trans('label.save')),
                            onClick: onFormSubmit,
                        },
                    }}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={() => {
                        setInputError(undefined);
                    }}
                />
            );
        }

        return (
            <CircularProgress />
        );
    }, [gatewayDefinitionInfoSettings, isEditing, isNewGatewayDef, onCancelEdit, onChangeEditingMode, onFormSubmit, onValidateJSON,
        selectedGatewayDef, trans]);


    return (
        <SlideOutComp
            title={selectedGatewayDef?.deviceClass}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
